import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthNonceHeader, ProfileRefCode, type RefCode } from '@/api/spec.ts';
import { environment } from '../../../environment.ts';
import useAppStore from '../../../store/rootStore.ts';
import { getValidNonce } from '../../../store/slices/authSlice.ts';
import { useAccountStatsStore } from '../../portfolio/store/AccountStatsStore.ts';

const fetchProfileRefCode = async (authNonce: string): Promise<RefCode> => {
  const response = await fetch(`${environment.apiUrl}${ProfileRefCode}`, {
    headers: {
      [AuthNonceHeader]: authNonce,
    },
  });

  if (response.status === 404) {
    return { code: '', limit: 0, used: 0, volume: '0', created_at: 0 };
  }

  if (!response.ok) {
    const error: Error & { response?: { status: number } } = new Error(`Failed to fetch referral code: ${response.status}`);
    error.response = { status: response.status };
    throw error;
  }

  return response.json();
};

export const useReferralCardData = () => {
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const authNonce = useAppStore((state) => getValidNonce(state.authNonce)?.value);
  const referralsEnabled = useAppStore((state) => state.config?.features?.referralsEnabled);
  const inviteVolumeThresholdRaw = useAppStore((state) => state.config?.rewards?.invite_volume_threshold);

  // Bucket inside the selector so this re-renders on tier crossings, not on every fill.
  const volumeTier = useAccountStatsStore(
    useCallback(
      (state) => {
        const tv = state.stats?.tv;
        if (!tv || !inviteVolumeThresholdRaw) return 0;
        try {
          const threshold = BigInt(inviteVolumeThresholdRaw);
          if (threshold === 0n) return 0;
          return Number(BigInt(tv) / threshold);
        } catch {
          return 0;
        }
      },
      [inviteVolumeThresholdRaw]
    )
  );

  const { data: refCode } = useQuery<RefCode>({
    queryKey: ['profile-ref-code', authNonce, volumeTier],
    queryFn: () => fetchProfileRefCode(authNonce!),
    enabled: isFullyAuthorized && Boolean(authNonce) && referralsEnabled,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const shouldDisplayData = isFullyAuthorized && Boolean(refCode) && referralsEnabled;

  const referralLink = useMemo(() => {
    if (!shouldDisplayData || !refCode?.code || typeof window === 'undefined') {
      return '';
    }
    const url = new URL('/trade', window.location.origin);
    url.searchParams.set('ref', refCode.code);
    return url.toString();
  }, [shouldDisplayData, refCode?.code]);

  return {
    referralLink,
    refCode,
    shouldDisplayData,
  };
};

export const useReferralLinkCopy = (referralLink: string) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setCopied(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [referralLink]);

  const handleCopy = useCallback(() => {
    if (!referralLink || typeof navigator === 'undefined' || !navigator.clipboard) return;

    navigator.clipboard
      .writeText(referralLink)
      .then(() => {
        setCopied(true);
        timeoutRef.current = setTimeout(() => {
          setCopied(false);
          timeoutRef.current = null;
        }, 2000);
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err);
      });
  }, [referralLink]);

  return {
    copied,
    handleCopy,
    isCopyDisabled: !referralLink,
  };
};

const SHARE_TEXT = 'You want to trade perps on perpl on the purple chain.';

export const useReferralShare = (referralLink: string, disabled: boolean) => {
  const openShareUrl = useCallback(
    (baseUrl: string) => {
      if (disabled || !referralLink || typeof window === 'undefined') return;
      const shareUrl = new URL(baseUrl);
      shareUrl.searchParams.set('url', referralLink);
      shareUrl.searchParams.set('text', SHARE_TEXT);
      window.open(shareUrl.toString(), '_blank', 'noopener,noreferrer');
    },
    [disabled, referralLink]
  );

  const handleShareTelegram = useCallback(() => {
    openShareUrl('https://t.me/share/url');
  }, [openShareUrl]);

  const handleShareTwitter = useCallback(() => {
    openShareUrl('https://x.com/intent/tweet');
  }, [openShareUrl]);

  return { handleShareTelegram, handleShareTwitter };
};
